/* eslint-disable max-lines, max-lines-per-function, complexity, max-nested-callbacks, no-nested-ternary, no-magic-numbers, no-restricted-syntax, id-length, id-denylist, @typescript-eslint/naming-convention, @typescript-eslint/no-floating-promises, @typescript-eslint/no-use-before-define, react-hooks/exhaustive-deps, react-perf/jsx-no-new-function-as-prop, react-perf/jsx-no-new-object-as-prop, react-perf/jsx-no-new-array-as-prop, react/jsx-no-leaked-render -- TODO(karl): [NX-7080] legacy checkout form predates current lint config (snake_case props from Sticky API, useState/useRef naming, file length, magic numbers, nested ternaries, id restrictions, react-perf inline handlers, leaked render guards). Disabled file-wide pending refactor under NX-7080; do not propagate this pattern to new files. */
import { Icon } from "@iconify/react";
import {
  AnalyticsEvents,
  CardBrand,
  ICreateOrderResult,
  PaymentMethod,
  useSubscription,
  useTranslation,
  useUserInfo,
} from "@next-platform/client-common-core";
import appLogger from "@next-platform/client-common-core/logger/appLogger";
import { BillingCycle } from "@next-platform/client-common-core/user/types";
import { useMedia } from "@next-platform/client-common-spicy-ui";
import { CalculateOrderTotalRequest, CheckoutEventRequest, NewOrderRequest, SubscriptionPlatform } from "@next-platform/packages-ts-types";
import { Checkbox, Divider, Spacer, Spinner } from "@nextui-org/react";
import React, { forwardRef, HTMLAttributes, useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useDebounceValue } from "usehooks-ts";

import { LocalizedLink } from "@/components";
import Trans from "@/components/Trans/Trans";
import { ROUTES } from "@/constants";
import { safeGetAttributionContext } from "@/features/attribution/safeGetAttributionContext";
import usePurchaseOptions from "@/hooks/usePurchaseOptions";
import useQueueLogEvent from "@/hooks/useQueueLogEvent";
import { cn, getCurrencySymbol, validateEmail, validatePostalCode } from "@/utils";
import getBillingCycleFromPlanId from "@/utils/getBillingCycleFromPlanId";

import { Col, Row, SpicyButton, Text } from "../customComponents";
import useApi from "../useApi";
import CardIcon from "./CardIcon";
import CreditCardSummary from "./CreditCardSummary";
import OrderSummary from "./OrderSummary";
import OrderSummaryItems from "./OrderSummaryItems";
import PaymentMethodRadioGroup from "./PaymentMethodRadioGroup";
import PersonalInfo from "./PersonalInfo";
import PersonalInfoSummary from "./PersonalInfoSummary";
import Sticky3dsModal from "./Sticky3dsModal";
import StickyCard from "./StickyCard";
import StickyKountJs from "./StickyKountJs";
import { CheckoutExitPoints, PriceData, PurchaseCompleteEvent, PurchaseEvent } from "./types";

const logger = appLogger.withTag("CheckoutForm");

const INVALID_CARD_ERROR_TYPE = "INVALID_CARD";

// Keep clicks on the terms link from bubbling up to the label's checkbox toggle.
const stopLinkPropagation = (event: { stopPropagation: () => void }) => event.stopPropagation();

// Russian users behind a VPN are geo-detected as another country, hiding RU payment
// methods. Selecting Russia as billing country re-queries purchase options with this override.
const RUSSIA_COUNTRY_CODE = "RU";

const otherPaymentMethods = [
  PaymentMethod.VOLT,
  PaymentMethod.PAYPORT,
  PaymentMethod.ANTRPAY,
  PaymentMethod.DIRECTA24,
  PaymentMethod.LUXFIN,
  PaymentMethod.PAYOK,
  PaymentMethod.INWIZO_ECOM,
  PaymentMethod.INWIZO_TPAY,
  PaymentMethod.INWIZO_SBP,
  PaymentMethod.UPGATE_ALIPAY,
  PaymentMethod.UPGATE_KAKAO,
  PaymentMethod.UPGATE_UPI,
  PaymentMethod.COINPAYMENTS,
  PaymentMethod.OVERPAY,
  PaymentMethod.FPAY_ALIPAY,
  PaymentMethod.FPAY_WECHAT,
  PaymentMethod.PAYSSION_GCASH_PH,
  PaymentMethod.PAYSSION_PAYMAYA_PH,
  PaymentMethod.PAYSSION_CARD_RU,
  PaymentMethod.PAYSSION_SBP_RU,
  PaymentMethod.PAYSSION_TBANK_RU,
  PaymentMethod.PAYSSION_YOOMONEY_RU,
  PaymentMethod.PAYSSION_SBERPAY_RU,
  PaymentMethod.DUKPAY_SBP_RU,
  PaymentMethod.DUKPAY_SBERPAY_RU,
  PaymentMethod.DUKPAY_TBANK_RU,
  PaymentMethod.DUKPAY_YOOMONEY_RU,
  PaymentMethod.DUKPAY_CARD_RU,
  PaymentMethod.STIXRAMP_CASH_APP_US,
  PaymentMethod.STIXRAMP_PAYPAL_US,
  PaymentMethod.PAYSSION_KAKAOPAY_KR,
  PaymentMethod.PAYSSION_SAMSUNGPAY_KR,
  PaymentMethod.PAYSSION_VIETQR_VN,
  PaymentMethod.PAYSSION_ENETS_SG,
  PaymentMethod.PAYSSION_PAYNOW_SG,
  PaymentMethod.PAYSSION_STCPAY_SA,
  PaymentMethod.PAYSSION_APPLEPAY_SA,
];

const CheckoutForm = forwardRef<HTMLFormElement, HTMLAttributes<HTMLFormElement>>(({ className, ...props }, ref) => {
  const { t } = useTranslation();

  const { onUserSubscribe, errorMessage, isLoading: isSubscriptionOrderLoading } = useSubscription();
  const { platform: subscriptionPlatform, email: userEmail, userId } = useUserInfo();
  const location = useLocation();
  const [purchaseOptionsFromState] = useState(location.state?.purchaseOptions || null);
  const [priceData, setPriceData] = useState<PriceData | null>(null);
  const [email, setEmail] = useState("");
  const [first_name, setFirstName] = useState("");
  const [last_name, setLastName] = useState("");
  const [country, setCountry] = useState(sessionStorage.getItem("checkout_country") || "");
  const purchaseOptionsCountryOverride = country === RUSSIA_COUNTRY_CODE ? RUSSIA_COUNTRY_CODE : null;
  const { purchaseOptions, isPurchaseOptionsLoading } = usePurchaseOptions({
    initialData: purchaseOptionsFromState ?? undefined,
    countryOverride: purchaseOptionsCountryOverride,
  });
  const [state, setState] = useState(sessionStorage.getItem("checkout_state") || "");
  const [stateOptional, setStateOptional] = useState(false);
  const [postal_code, setPostalCode] = useState("");
  const [couponCode, setCouponCode] = useState("");
  const [creditCardToken, setCreditCardToken] = useState<string | null>(null);
  const [kountSessionId, setKountSessionId] = useState<string | null>(null);
  const [isCardValid, setIsCardValid] = useState(false);
  const [isCheckboxChecked, setIsCheckboxChecked] = useState(false);
  const [isChangePlanLoading, setIsChangePlanLoading] = useState(false);
  const [personalEditingEnabled, setPersonalEditingEnabled] = useState(true);
  const [creditCardEditingEnabled, setCreditCardEditingEnabled] = useState(true);
  const [is3dsModalOpen, setIs3dsModalOpen] = useState(false);
  const [iframe3dsUrl, setIframe3dsUrl] = useState("");
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>(PaymentMethod.CARD);
  const [orderError, setOrderError] = useState("");
  const { callApi, jsonApiService, isLoading: isCalculationLoading } = useApi<PriceData>();
  const { callApi: callAltRedirectOrderApi, isLoading: isAltRedirectOrderLoading } = useApi<{ error_message?: string; url?: string }>();
  const triggerCheckoutAbandon = useRef(true);
  const checkoutExitPoint = useRef<CheckoutExitPoints | null>(null);
  const isCardProcessing = isSubscriptionOrderLoading || isAltRedirectOrderLoading;

  const { logEvent } = useQueueLogEvent();
  const [searchParams] = useSearchParams();
  const params = Object.fromEntries(searchParams.entries());
  const [debouncedEmail, setDeboucedEmail] = useDebounceValue(email, 600);
  const [debouncedPostalCode, setDebouncedPostalCode] = useDebounceValue(postal_code, 600);
  const navigate = useNavigate();
  const { isMobile } = useMedia();
  const defaultPlanId = purchaseOptions?.default_plan_id as string;
  // Destructure with default values
  const { plan_id = defaultPlanId, o_id, coupon: couponFromQuery } = params;

  const [initialPlanId, setInitialPlanId] = useState(plan_id);
  const subscriptionError = errorMessage || (orderError === INVALID_CARD_ERROR_TYPE ? t("subscription:checkout.invalidCardError") : orderError);

  //we get the user email from redux state

  useEffect(() => {
    setEmail(userEmail || "");
  }, [userEmail]);
  useEffect(() => {
    setDeboucedEmail(email);
  }, [email]);
  useEffect(() => {
    setDebouncedPostalCode(postal_code);
  }, [postal_code]);

  //we hydrate the form from session storage
  useEffect(() => {
    setFirstName(sessionStorage.getItem("checkout_first_name") || "");
    setLastName(sessionStorage.getItem("checkout_last_name") || "");
    setCountry(sessionStorage.getItem("checkout_country") || "");
    setState(sessionStorage.getItem("checkout_state") || "");
    setPostalCode(sessionStorage.getItem("checkout_postal_code") || "");
  }, []);

  //if we have subscriptionPlatform we load the user info
  useEffect(() => {
    if (subscriptionPlatform === SubscriptionPlatform.STICKY) {
      setPersonalEditingEnabled(false);
      setCreditCardEditingEnabled(false);
    }
  }, [subscriptionPlatform]);

  useEffect(() => {
    if (initialPlanId !== plan_id) {
      setInitialPlanId(plan_id);
      setIsChangePlanLoading(true);
    }
  }, [plan_id]);

  useEffect(() => {
    if (couponFromQuery) {
      setCouponCode(couponFromQuery);
    }
  }, [couponFromQuery]);

  useEffect(() => {
    //we'll check price once we have enough required fields
    if (plan_id && debouncedEmail) {
      //we only check once user is loaded

      const payload: CalculateOrderTotalRequest = {
        plan_id,
        offer_id: o_id ?? "",
        email: debouncedEmail,
        state,
        country,
        postal_code: debouncedPostalCode,
        promo_code: couponCode,
        platform: subscriptionPlatform as SubscriptionPlatform,
      };

      //we'll calculate the price by calling the price api
      callApi(jsonApiService.createResource, "checkout/calculate", payload as unknown as Record<string, unknown>)
        .then((data) => {
          setPriceData(data ?? null);
          if (data?.on_file && !personalEditingEnabled) {
            const { first_name, last_name, country, state, postal_code } = data.on_file;
            if (first_name) {
              setFirstName(first_name);
            }
            if (last_name) {
              setLastName(last_name);
            }
            if (country) {
              setCountry(country);
            }
            if (state) {
              setState(state);
            }
            if (postal_code) {
              setPostalCode(postal_code);
            }
            checkoutExitPoint.current = CheckoutExitPoints.PAYMENT_INFO;
          }
        })
        .catch((err) => {
          logger.error("Error while calculating price", err);
          setOrderError(err.message);
        })
        .finally(() => {
          setIsChangePlanLoading(false);
        });
    }
  }, [debouncedEmail, state, country, debouncedPostalCode, couponCode, plan_id, o_id, subscriptionPlatform]);

  //we clear the order error if these value change
  useEffect(() => {
    setOrderError("");
  }, [creditCardToken, personalEditingEnabled, creditCardEditingEnabled]);

  useEffect(() => {
    if (isCheckboxChecked) {
      //if we don't have a token we display an error to help user
      if (!creditCardToken && creditCardEditingEnabled && paymentMethod === PaymentMethod.CARD) {
        setOrderError(INVALID_CARD_ERROR_TYPE);
      }
    }
  }, [isCheckboxChecked]);

  useEffect(() => {
    // we set the default to first option received from backend when it's available
    if (purchaseOptions?.payment_methods?.length) {
      setPaymentMethod(purchaseOptions?.payment_methods?.[0] as PaymentMethod);
    }
  }, [purchaseOptions]);

  useEffect(() => {
    if (location.state) {
      // clear state
      navigate(`${location.pathname}${location.search}`, { replace: true, state: null });
    }

    return () => {
      // to trigger log event when checkout is abandoned
      if (triggerCheckoutAbandon.current) {
        logEvent(AnalyticsEvents.CHECKOUT_EXIT_ABANDON, { exit_point: checkoutExitPoint.current });
      }
    };
  }, []);

  const updatePersonalInfo = (field: string, value: string) => {
    //we persist to session storage
    sessionStorage.setItem(`checkout_${field}`, value);
    checkoutExitPoint.current = CheckoutExitPoints.USER_DETAILS;

    switch (field) {
      case "email":
        setEmail(value);
        break;
      case "first_name":
        setFirstName(value);
        break;
      case "last_name":
        setLastName(value);
        break;
      case "country":
        setCountry(value);
        break;
      case "state":
        setState(value);
        break;
      case "postal_code":
        setPostalCode(value);
        setOrderError("");
        break;
      case "state_optional":
        setStateOptional(value === "true");
        break;
    }
  };

  const sendPurchaseAnalytics = (payload: PurchaseEvent) => {
    logEvent(AnalyticsEvents.PURCHASE, payload);
  };

  const handleSuccessfulOrder = (data: ICreateOrderResult) => {
    const plan = priceData?.line_items?.map((d) => d?.name)?.join(",") || "";
    const itemPrice = priceData?.line_items[0]?.total;
    const { currency, order_id, orderTotal } = data;

    const purchaseCompletePayload: PurchaseCompleteEvent = {
      user_id: userId,
      plan_id: plan,
      transaction_id: order_id,
      total_amount: orderTotal,
      currency: currency,
      timestamp: new Date().toISOString(),
    };

    // sending message to native app for analytics
    if (window?.ReactNativeWebView) {
      const message = JSON.stringify({
        event_name: AnalyticsEvents.CHECKOUT_PURCHASE_COMPLETE,
        ...purchaseCompletePayload,
      });
      window.ReactNativeWebView.postMessage(message);
    }

    const purchaseEventPayload: PurchaseEvent = {
      currency: data?.currency,
      value: Number(data?.orderTotal),
      transaction_id: order_id,
      items: [
        {
          item_id: plan_id ?? "",
          item_name: plan,
          price: Number(itemPrice),
          quantity: 1,
        },
      ],
      timestamp: new Date().toISOString(),
    };
    sendPurchaseAnalytics(purchaseEventPayload);

    //we add all fields from response to query string and navigate to success page
    const query = new URLSearchParams();
    Object.entries(data).forEach(([key, value]) => {
      if (typeof key === "string" && typeof value === "string") {
        query.set(key, value);
      }
    });

    const date = Date.now().toString();
    query.set("plan", plan);
    query.set("date", date);

    // `plan` above is the human-readable product name; attribution needs
    // the SKU (`plan_id`), which is not part of the order response.
    if (plan_id !== undefined && plan_id !== "") {
      query.set("plan_id", plan_id);
    }
    // "Sticky" with CampaignsWell's capitalisation, not our lowercase
    // `SubscriptionPlatform` slug — they name providers by brand.
    //
    // Hardcoded rather than read from `subscriptionPlatform`: that value is the
    // platform of the user's *existing* subscription (undefined for a
    // first-time subscriber). This handler only runs on the Sticky card / 3DS
    // paths — the alternative processors redirect off-site and never reach it —
    // so Sticky is correct here by construction.
    query.set("payment_system", "Sticky");

    // Sticky returns `customerId` as a number, which the string-only loop
    // above skips. Set it explicitly so CampaignsWell gets `customer_id`.
    const { customerId } = data;
    if (typeof customerId === "string" || typeof customerId === "number") {
      query.set("customerId", String(customerId));
    }

    const grossAmount = Number.parseFloat(orderTotal ?? "");
    const taxAmount = Number.parseFloat(priceData?.tax?.total ?? "");
    if (Number.isFinite(grossAmount) && Number.isFinite(taxAmount) && taxAmount > 0) {
      query.set("netAmount", (grossAmount - taxAmount).toFixed(2));
    }

    triggerCheckoutAbandon.current = false;
    navigate(`/checkout/success?${query.toString()}`);
  };

  const submitOrder = async () => {
    // Non-blocking read — backend snapshots this onto
    // Subscription.attribution as an immutable per-purchase record.
    const attribution = await safeGetAttributionContext();
    const payload: NewOrderRequest = {
      payment_token: creditCardToken as string,
      email,
      first_name,
      last_name,
      offer_id: o_id ?? "",
      plan_id: plan_id ?? "",
      state,
      country,
      postal_code,
      promo_code: couponCode,
      platform: subscriptionPlatform as SubscriptionPlatform,
      session_id: kountSessionId as string,
      ...(attribution !== undefined && { attribution }),
    };
    const data = await onUserSubscribe(payload);
    if (data?.error_message) {
      setOrderError(data.error_message || "");
    }
    if (data?.resp_msg === "redirect" && data?.iframe_3ds_url) {
      setIframe3dsUrl(data.iframe_3ds_url);
      setIs3dsModalOpen(true);
    }
    if (data?.resp_msg === "Approved") {
      handleSuccessfulOrder(data);
    }
  };

  const on3dsSuccess = (data: Record<string, unknown>) => {
    setIs3dsModalOpen(false);
    handleSuccessfulOrder(data);
  };
  const on3dsFailure = (error_message: string) => {
    setIs3dsModalOpen(false);
    setOrderError(error_message);
  };

  const altPaymentRedirect = async (method: PaymentMethod) => {
    // Non-blocking read — backend snapshots this onto
    // Subscription.attribution as an immutable per-purchase record.
    const attribution = await safeGetAttributionContext();
    let api_endpoint: string;
    switch (method) {
      case PaymentMethod.VOLT:
        api_endpoint = "checkout/volt_new_payment";
        break;
      case PaymentMethod.PAYPORT:
        api_endpoint = "checkout/payport_new_payment";
        break;
      case PaymentMethod.ANTRPAY:
        api_endpoint = "checkout/antrpay_new_payment";
        break;
      case PaymentMethod.DIRECTA24:
        api_endpoint = "checkout/directa24_new_payment";
        break;
      case PaymentMethod.LUXFIN:
        api_endpoint = "checkout/luxfin_new_payment";
        break;
      case PaymentMethod.PAYOK:
        api_endpoint = "checkout/payok_new_payment";
        break;
      case PaymentMethod.INWIZO_ECOM:
      case PaymentMethod.INWIZO_TPAY:
      case PaymentMethod.INWIZO_SBP:
        api_endpoint = "checkout/inwizo_new_payment";
        break;
      case PaymentMethod.UPGATE_ALIPAY:
      case PaymentMethod.UPGATE_KAKAO:
      case PaymentMethod.UPGATE_UPI:
        api_endpoint = "checkout/upgate_new_payment";
        break;
      case PaymentMethod.COINPAYMENTS:
        api_endpoint = "checkout/coinpayments_new_payment";
        break;
      case PaymentMethod.OVERPAY:
        api_endpoint = "checkout/overpay_new_payment";
        break;
      case PaymentMethod.FPAY_ALIPAY:
      case PaymentMethod.FPAY_WECHAT:
        api_endpoint = "checkout/fpay_new_payment";
        break;
      case PaymentMethod.PAYSSION_GCASH_PH:
      case PaymentMethod.PAYSSION_PAYMAYA_PH:
      case PaymentMethod.PAYSSION_CARD_RU:
      case PaymentMethod.PAYSSION_SBP_RU:
      case PaymentMethod.PAYSSION_TBANK_RU:
      case PaymentMethod.PAYSSION_YOOMONEY_RU:
      case PaymentMethod.PAYSSION_SBERPAY_RU:
      case PaymentMethod.PAYSSION_KAKAOPAY_KR:
      case PaymentMethod.PAYSSION_SAMSUNGPAY_KR:
      case PaymentMethod.PAYSSION_VIETQR_VN:
      case PaymentMethod.PAYSSION_STCPAY_SA:
      case PaymentMethod.PAYSSION_APPLEPAY_SA:
      case PaymentMethod.PAYSSION_ENETS_SG:
      case PaymentMethod.PAYSSION_PAYNOW_SG:
        api_endpoint = "checkout/payssion_new_payment";
        break;
      case PaymentMethod.DUKPAY_SBP_RU:
      case PaymentMethod.DUKPAY_SBERPAY_RU:
      case PaymentMethod.DUKPAY_TBANK_RU:
      case PaymentMethod.DUKPAY_YOOMONEY_RU:
      case PaymentMethod.DUKPAY_CARD_RU:
        api_endpoint = "checkout/dukpay_new_payment";
        break;
      case PaymentMethod.STIXRAMP_CASH_APP_US:
      case PaymentMethod.STIXRAMP_PAYPAL_US:
        api_endpoint = "checkout/stixramp_new_payment";
        break;
      default:
        return;
    }

    const payload: CheckoutEventRequest = {
      plan_id: plan_id ?? "",
      offer_id: o_id ?? "",
      email,
      first_name,
      last_name,
      state,
      country,
      postal_code,
      promo_code: couponCode,
      payment_option: method,
      ...(attribution !== undefined && { attribution }),
    };

    callAltRedirectOrderApi(jsonApiService.createResource, api_endpoint, payload as unknown as Record<string, unknown>)
      .then((data) => {
        if (data?.error_message) {
          setOrderError(data.error_message || "");
        }
        if (data?.url) {
          triggerCheckoutAbandon.current = false;
          window.location.href = data.url;
        }
      })
      .catch((error) => {
        setOrderError(error?.message);
      });
  };

  const onPaymentButtonClicked = () => {
    // Purchase options (and the valid payment methods) are being refetched after a
    // billing-country change. paymentMethod still holds the pre-refetch value, so
    // proceeding here could redirect with a method that no longer matches country.
    if (isPurchaseOptionsLoading) {
      return;
    }

    // sending message to native app for analytics
    if (window?.ReactNativeWebView) {
      const message = JSON.stringify({
        event_name: AnalyticsEvents.SUBSCRIPTION_CHECKOUT_START,
        user_id: userId,
        plan_id,
        currency: priceData?.currency,
        plan_price: priceData?.total,
      });
      window.ReactNativeWebView.postMessage(message);
    }

    logEvent(AnalyticsEvents.CHECKOUT_PROCEED, { cart_value: totalAmount });

    if (paymentMethod === PaymentMethod.CARD) {
      submitOrder();
    } else if (otherPaymentMethods.includes(paymentMethod)) {
      altPaymentRedirect(paymentMethod);
    }
  };

  const isSubmitButtonDisabled =
    (paymentMethod === PaymentMethod.CARD && creditCardToken === null && !isCardValid && !priceData?.on_file?.order_id) ||
    orderError !== "" ||
    !isCheckboxChecked ||
    isCardProcessing ||
    isPurchaseOptionsLoading ||
    postal_code === "" ||
    !validatePostalCode(postal_code, country) ||
    !validateEmail(email) ||
    country === "" ||
    country === "null" ||
    (state === "" && !stateOptional) ||
    first_name?.trim() === "" ||
    last_name?.trim() === "";

  const billingPeriod = getBillingCycleFromPlanId(plan_id);
  const billingPeriodTextByCycle: Record<BillingCycle, string> = {
    [BillingCycle.MONTHLY]: t("subscription:checkout.monthly"),
    [BillingCycle.QUARTERLY]: t("subscription:checkout.quarterly"),
    [BillingCycle.ANNUAL]: t("subscription:checkout.annually"),
  };
  const billingPeriodText = (billingPeriodTextByCycle[billingPeriod] ?? t("subscription:checkout.monthly")).toLowerCase();

  const currencySymbol = getCurrencySymbol(priceData?.currency || "USD");
  const plan = priceData?.line_items.map((d) => d.name).join(",") || "";
  const totalAmount = parseFloat(priceData?.total || "").toFixed(2);

  const PersonalInfoComponent = personalEditingEnabled || !priceData?.on_file ? PersonalInfo : PersonalInfoSummary;

  const radioGroupOnChangeHandler = (value: string) => {
    checkoutExitPoint.current = CheckoutExitPoints.PAYMENT_INFO;
    setPaymentMethod(value as PaymentMethod);
  };

  const handleCardValidation = useCallback((isValid: boolean) => {
    setIsCardValid(isValid);
    checkoutExitPoint.current = CheckoutExitPoints.PAYMENT_INFO;
  }, []);

  const handleTermsToggle = useCallback(() => {
    setIsCheckboxChecked((prev) => !prev);
    checkoutExitPoint.current = CheckoutExitPoints.CHECKBOX_MARK;
  }, []);

  const TermsAndServices = () => {
    return (
      <div className="flex items-start gap-2 mb-2">
        <Checkbox
          size="sm"
          classNames={{ base: "flex-shrink-0", icon: "text-white" }}
          isSelected={isCheckboxChecked}
          onValueChange={handleTermsToggle}
        />
        <Text className="text-xs cursor-pointer" onClick={handleTermsToggle}>
          <Trans
            i18nKey="subscription:checkout.termsAgreement"
            components={{
              termsLink: (
                <LocalizedLink
                  to={ROUTES.terms}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="terms-and-conditions"
                  className="text-link underline"
                  onClick={stopLinkPropagation}
                />
              ),
            }}
          />
        </Text>
      </div>
    );
  };

  return (
    <section className="flex w-full max-w-5xl flex-col lg:flex-row lg:gap-8">
      <div className="w-full">
        {isMobile && (
          <>
            <Text b variant={"xxl"}>
              <Trans i18nKey="subscription:checkout.heading" />
            </Text>
            <Col className="bg-[#9DB2CE40] p-4 rounded-xl gap-3 my-4">
              {(isCalculationLoading && !couponCode) || !priceData?.total ? (
                <Spinner />
              ) : (
                <>
                  <Row justify={"between"} style={undefined}>
                    <OrderSummaryItems
                      items={priceData?.line_items || []}
                      billingPeriod={billingPeriod}
                      updateCoupon={(value) => setCouponCode(value)}
                      setIsChangePlanLoading={setIsChangePlanLoading}
                      purchaseOptions={purchaseOptions!}
                    />
                    <Text type="capitalize">{billingPeriodText}</Text>
                  </Row>
                  <Row justify={"between"} style={undefined}>
                    <Text type="font-[600]">
                      <Trans i18nKey="subscription:checkout.totalAmount" />
                    </Text>
                    <Text type="font-[600]">
                      {currencySymbol}
                      {totalAmount}
                    </Text>
                  </Row>
                </>
              )}
            </Col>
            <Text variant={"regular"} type={"font-[600]"}>
              <Trans i18nKey="subscription:checkout.personalInformation" />
            </Text>
            <Spacer y={1.5} />
          </>
        )}
        <form ref={ref} className={cn("flex flex-col gap-4 py-0", className)} {...props}>
          <PersonalInfoComponent
            email={email}
            country={country}
            state={state}
            stateOptional={stateOptional}
            postal_code={postal_code}
            first_name={first_name}
            last_name={last_name}
            updatePersonalInfo={updatePersonalInfo}
            onChangeButtonClick={() => setPersonalEditingEnabled(true)}
          />
          {isPurchaseOptionsLoading ? (
            <div className="flex justify-center py-2" data-testid="CheckoutForm-PaymentMethodsLoader">
              <Spinner />
            </div>
          ) : purchaseOptions?.payment_methods?.some((method) => otherPaymentMethods.includes(method as PaymentMethod)) ? (
            <PaymentMethodRadioGroup
              paymentMethods={purchaseOptions?.payment_methods || [PaymentMethod.CARD]}
              value={paymentMethod}
              onValueChange={(value) => radioGroupOnChangeHandler(value as PaymentMethod)}
            />
          ) : null}
          {paymentMethod === PaymentMethod.CARD && (
            <>
              {(creditCardEditingEnabled || !priceData?.on_file?.credit_card) && (
                <>
                  <Row gap={1}>
                    <Text variant={"regular"} type={"font-[600]"}>
                      <Trans i18nKey="subscription:checkout.cardInfo" />
                    </Text>
                    {Object.values(CardBrand).map((brand) => (
                      <CardIcon key={brand} brand={brand} width="40" />
                    ))}
                  </Row>
                  <StickyCard setIsCardValid={handleCardValidation} setCreditCardToken={setCreditCardToken} />
                </>
              )}
              {!creditCardEditingEnabled && priceData?.on_file?.credit_card && (
                <CreditCardSummary
                  last_4={priceData?.on_file?.credit_card?.last_4}
                  type={priceData?.on_file?.credit_card?.type}
                  exp={priceData?.on_file?.credit_card?.exp}
                  onChangeButtonClick={() => setCreditCardEditingEnabled(true)}
                />
              )}
            </>
          )}
          {[PaymentMethod.VOLT, PaymentMethod.DIRECTA24].includes(paymentMethod) && (
            <Text className="text-small text-default-300">
              <Trans i18nKey="subscription:checkout.voltNote" components={{ bold: <b /> }} />
            </Text>
          )}
          {paymentMethod === PaymentMethod.PAYPORT && (
            <Text className="text-small text-default-300">
              <Trans i18nKey="subscription:checkout.payportNote" components={{ bold: <b /> }} />
            </Text>
          )}
        </form>
      </div>
      {isMobile && (
        <>
          <Spacer y={6} />
          <Divider />
          <Spacer y={4} />
          <TermsAndServices />
          <Spacer y={3} />
          <Divider />
        </>
      )}

      <Col className="w-full lg:w-[340px] lg:flex-none">
        <div className={"rounded-medium px-2 py-4 md:px-6 md:py-8 " + (!isMobile && "bg-content2 dark:bg-content1")}>
          <OrderSummary
            isCalculating={isCalculationLoading}
            billingPeriod={billingPeriod}
            allowCoupon={couponFromQuery === undefined}
            discounts={priceData?.discounts || []}
            subtotal={parseFloat(priceData?.subtotal || "")}
            total={parseFloat(priceData?.total || "")}
            tax={priceData?.tax}
            currency={priceData?.currency || "USD"}
            items={priceData?.line_items || []}
            updateCoupon={(value) => setCouponCode(value)}
            currencySymbol={currencySymbol}
            plan={plan}
            isChangePlanLoading={isChangePlanLoading}
            purchaseOptions={purchaseOptions}
          />

          <div className="mt-4">
            {subscriptionError && <div className="flex justify-center text-danger-300 pb-4">{subscriptionError}</div>}
            {isMobile && (
              <>
                <Divider />
                <Divider />
                <Row className="justify-between items-center py-6">
                  <Text b variant={"regular"}>
                    {totalAmount !== "NaN" ? currencySymbol + totalAmount : ""}
                  </Text>

                  <SpicyButton
                    variant="primary_large"
                    color="primary_strong"
                    isDisabled={isSubmitButtonDisabled}
                    onClick={() => onPaymentButtonClicked()}
                    isLoading={isCardProcessing}
                  >
                    <Text b variant="md" type="white">
                      {isCardProcessing ? (
                        <Trans i18nKey="subscription:checkout.processing" />
                      ) : paymentMethod === PaymentMethod.CARD ? (
                        <Trans i18nKey="subscription:checkout.subscribe" />
                      ) : (
                        <Trans i18nKey="subscription:checkout.continue" />
                      )}
                    </Text>
                  </SpicyButton>
                </Row>
              </>
            )}
            {!isMobile && (
              <>
                <TermsAndServices />
                <SpicyButton
                  fullWidth
                  color="primary_strong"
                  radius="sm"
                  size="lg"
                  isDisabled={isSubmitButtonDisabled}
                  onClick={() => onPaymentButtonClicked()}
                >
                  {isCardProcessing ? (
                    <Trans i18nKey="subscription:checkout.processing" />
                  ) : paymentMethod === PaymentMethod.CARD ? (
                    <Trans i18nKey="subscription:checkout.subscribe" />
                  ) : (
                    <Trans i18nKey="subscription:checkout.continue" />
                  )}
                </SpicyButton>
              </>
            )}

            {paymentMethod === PaymentMethod.CARD && (
              <div className="text-xs text-default-500 text-center">
                <div className={"mt-4 mb-4 " + (isMobile && "bg-[#9DB2CE40] rounded-xl p-2")}>
                  <Trans i18nKey="subscription:checkout.autoRenewalNote" values={{ billingPeriod: billingPeriodText, statementName: "NDAI.CHAT" }} />
                </div>
              </div>
            )}
          </div>
          <Row className="mt-6 items-center justify-center gap-1 pb-5">
            <Icon className="text-green-600" icon="solar:shield-check-bold" width={20} />
            <p className="text-small text-gray-800">
              <Trans i18nKey="subscription:checkout.securePayment" />
            </p>
          </Row>
        </div>
      </Col>
      <Sticky3dsModal
        isOpen={is3dsModalOpen}
        onClose={() => setIs3dsModalOpen(false)}
        onSuccess={on3dsSuccess}
        onFailure={on3dsFailure}
        iframeUrl={iframe3dsUrl}
      />
      {paymentMethod === PaymentMethod.CARD && <StickyKountJs setKountSessionId={setKountSessionId} />}
    </section>
  );
});
CheckoutForm.displayName = "CheckoutForm";

export default CheckoutForm;
